"use client";

import React from "react";
import { ArrowRight, Wrench, ShieldCheck, Gauge } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function PartPricing() {
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = {
    duration: shouldReduceMotion ? 0.01 : 0.3,
    ease: [0.16, 1, 0.3, 1]
  };
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: transitionConfig
    }
  };
  const specs = [{
    title: "Electrical & Controls",
    desc: "Inverters, safety switches, and master operator consoles",
    icon: Gauge
  }, {
    title: "Drive & Motion Gear",
    desc: "Gearboxes, motors, couplings, and structural mountings",
    icon: Wrench
  }, {
    title: "Protective Surrounds",
    desc: "Commercial inflatables, commercial air pumps, and bull skins",
    icon: ShieldCheck
  }];
  return <section id="replacementpartpricing" data-nav="light" className="relative bg-background py-16 md:py-24 border-b border-border selection:bg-primary selection:text-primary-foreground">
      <div className="container mx-auto max-w-4xl px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants}>
          {/* Technical Spec Plate Card */}
          <Card className="relative overflow-hidden rounded-none border border-border bg-card p-0 shadow-sm">
            {/* Machined Corner Registration Ticks */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-primary pointer-events-none z-10" aria-hidden="true" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-primary pointer-events-none z-10" aria-hidden="true" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-primary pointer-events-none z-10" aria-hidden="true" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-primary pointer-events-none z-10" aria-hidden="true" />

            {/* Industrial Header Bar */}
            <div className="flex flex-wrap items-center justify-between border-b border-border bg-muted/60 px-6 py-3 text-xs font-mono uppercase tracking-wider text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-primary rounded-none" />
                <span>SPEC SHEET: PRICING-REF-03</span>
              </div>
              <div className="flex items-center gap-4">
                <span>COMMERCIAL REPLACEMENT PARTS</span>
              </div>
            </div>

            <CardContent className="p-6 sm:p-10 md:p-12 space-y-8">
              {/* Heading Area */}
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold block mb-2">
                  Inventory &amp; Valuation
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
                  Mechanical Bull Replacement Part Pricing
                </h2>
              </div>

              {/* Technical Rule */}
              <Separator className="bg-border" />

              {/* Body Copy Section */}
              <div className="space-y-5 text-base sm:text-lg leading-relaxed text-foreground font-sans">
                <p>
                  Replacement-part prices vary widely because a small switch, a complete controller and a replacement inflatable solve very different problems. For that reason, there is no useful single price for &apos;mechanical bull parts.&apos;
                </p>
                <p className="text-muted-foreground">
                  Compare the actual component, its condition, what comes with it and whether it is genuinely compatible with your equipment. A lower-priced part can become expensive very quickly if it does not fit the machine you own.
                </p>
                <p className="text-muted-foreground">
                  Where products are currently available, Mechanical Bull Shop displays pricing directly on the listing so buyers can compare actual inventory.
                </p>
              </div>

              {/* Component Categories Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                {specs.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i} className="border border-border bg-muted/30 p-4 rounded-none">
                      <div className="flex items-center gap-2.5 mb-2">
                        <Icon className="w-4 h-4 text-primary shrink-0" />
                        <h3 className="font-serif font-bold text-sm sm:text-base uppercase tracking-wider text-foreground">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
                        {item.desc}
                      </p>
                    </div>;
              })}
              </div>

              {/* Callout Link Band */}
              <div className="border border-border/80 bg-accent/40 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start sm:items-center gap-3">
                  <span className="w-1.5 h-6 bg-primary shrink-0 hidden sm:block" />
                  <p className="text-sm sm:text-base text-foreground font-medium">
                    For complete-machine pricing, read the{" "}
                    <Link to="/MechanicalBullPriceGuidePage" className="inline-flex items-center font-bold text-primary hover:underline underline-offset-4 transition-colors">
                      Mechanical Bull Price Guide
                      <ArrowRight className="ml-1 w-4 h-4 inline" />
                    </Link>
                    .
                  </p>
                </div>
                <div className="font-mono text-2xs uppercase text-muted-foreground tracking-widest shrink-0 self-end sm:self-center">
                  REV 2026.1
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}